import { usePermissionStore } from '../stores/permissionStore';
/**
 * BarmetHub Sidebar — RBAC-aware navigation
 * SUPERADMIN sees full navigation. EMPLOYEE sees filtered nav.
 * Updated for BHM-HMS-REQ-032.
 */
import { useEffect } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import { isSuperAdmin, type NavItem, filterNav } from '../utils/permissions';
import { APP_VERSION } from '../version';
import clsx from 'clsx';

function MaterialIcon({ name, className = '', filled = false }: { name: string; className?: string; filled?: boolean }) {
  return (
    <span
      aria-hidden="true"
      className={clsx('material-symbols-outlined', className)}
      style={filled ? { fontVariationSettings: "'FILL' 1" } : undefined}
    >
      {name}
    </span>
  );
}

// ── Navigation definition ───────────────────────────────────────────────
interface NavGroup {
  id: string;
  label: string;
  items: NavItem[];
}

const navGroups: NavGroup[] = [
  {
    id: 'principal',
    label: 'Principal',
    items: [
      { to: '/nexus', icon: 'bolt', label: 'Nexus', permission: 'NEXUS_USE' },
      { to: '/dashboard', icon: 'home', label: 'Dashboard', permission: null },
      { to: '/mi-trabajo', icon: 'task_alt', label: 'Mi trabajo', permission: 'TASKS_VIEW' },
      { to: '/proyectos', icon: 'folder_open', label: 'Proyectos', permission: 'PROJECTS_VIEW' },
      { to: '/dashboards-clientes', icon: 'analytics', label: 'Dashboards de clientes', permission: null, superAdminOnly: true },
    ],
  },
  {
    id: 'operations',
    label: 'Operaciones',
    items: [
      { to: '/jornada', icon: 'schedule', label: 'Mi jornada', permission: 'TIME_VIEW_OWN' },
      { to: '/control-tiempos', icon: 'monitoring', label: 'Control de tiempos', permission: null, superAdminOnly: true },
      { to: '/operaciones/tiempo', icon: 'timer', label: 'Tiempo', permission: 'TIME_VIEW_OWN' },
      { to: '/operaciones/revision-tiempos', icon: 'fact_check', label: 'Revisión de tiempos', permission: 'TIME_REVIEW' },
      { to: '/planificacion', icon: 'calendar_month', label: 'Planificación', permission: 'TASKS_VIEW' },
      { to: '/operaciones/administracion', icon: 'account_balance', label: 'Administración', permission: 'ADMINISTRATION_VIEW' },
      { to: '/operaciones/crm', icon: 'contacts', label: 'CRM', permission: 'CRM_VIEW' },
      { to: '/operaciones/marketing', icon: 'campaign', label: 'Marketing', permission: 'MARKETING_VIEW' },
      { to: '/operaciones/webs', icon: 'language', label: 'Webs', permission: 'WEBS_VIEW' },
    ],
  },
  {
    id: 'automation',
    label: 'Automatización',
    items: [
      { to: '/automatizacion/workflows', icon: 'automation', label: 'Automatizaciones', permission: 'AUTOMATIONS_VIEW' },
      { to: '/automatizacion/ia', icon: 'neurology', label: 'IA', permission: 'AI_USE' },
    ],
  },
  {
    id: 'nexus',
    // Configuración técnica del orquestador: solo SUPERADMIN (REQ-042 §3).
    label: 'Nexus Core',
    items: [
      { to: '/nexus/admin', icon: 'monitoring', label: 'Ejecuciones Nexus', permission: 'NEXUS_ADMIN', superAdminOnly: true },
      { to: '/nexus/hermes', icon: 'hub', label: 'Runtime de agentes', permission: 'HERMES_VIEW', superAdminOnly: true },
      { to: '/nexus/agentes', icon: 'smart_toy', label: 'Agentes', permission: 'AGENTS_VIEW', superAdminOnly: true },
      { to: '/nexus/skills', icon: 'school', label: 'Skills', permission: 'SKILLS_VIEW', superAdminOnly: true },
      { to: '/nexus/mcp', icon: 'terminal', label: 'MCP', permission: 'MCP_VIEW', superAdminOnly: true },
      { to: '/nexus/tools', icon: 'build', label: 'Tools', permission: 'TOOLS_USE', superAdminOnly: true },
    ],
  },
  {
    id: 'resources',
    label: 'Recursos',
    items: [
      { to: '/recursos/lasspass', icon: 'key', label: 'Lasspass', permission: 'LASSPASS_VIEW' },
      { to: '/recursos/factory-prompts', icon: 'auto_awesome', label: 'Factory Prompts', permission: 'FACTORY_PROMPTS_VIEW' },
    ],
  },
];

const settingsNav: NavItem[] = [
  { to: '/ajustes/mi-cuenta', icon: 'person', label: 'Mi cuenta', permission: null },
  { to: '/ajustes/integraciones', icon: 'hub', label: 'Integraciones', permission: 'INTEGRATIONS_VIEW' },
  { to: '/ajustes/accesos-empleados', icon: 'badge', label: 'Accesos empleados', permission: 'EMPLOYEES_VIEW', superAdminOnly: true },
  { to: '/ajustes/seguridad', icon: 'shield', label: 'Seguridad del sistema', permission: null, superAdminOnly: true },
  { to: '/ajustes/registro', icon: 'history', label: 'Registro de actividad', permission: 'ACTIVITY_LOG_VIEW', superAdminOnly: true },
  { to: '/ajustes/estado', icon: 'monitor_heart', label: 'Estado del sistema', permission: null, superAdminOnly: true },
];

// ── NavLink item ────────────────────────────────────────────────────────
function SidebarLink({ to, icon, label }: { to: string; icon: string; label: string }) {
  return (
    <NavLink
      to={to}
      aria-label={label}
      title={label}
      className={({ isActive }) => clsx(
        'flex items-center gap-3 rounded-lg px-3 py-2 font-label-caps text-label-caps transition-all',
        isActive
          ? 'bg-gradient-to-r from-[#FF5E1E]/15 to-[#D946EF]/15 text-on-surface font-bold border-l-2 border-[#FF5E1E]'
          : 'text-on-surface-variant hover:bg-surface-container-highest hover:text-on-surface'
      )}
    >
      <MaterialIcon name={icon} />
      <span className="sidebar-label">{label}</span>
    </NavLink>
  );
}

// ── Main Sidebar ────────────────────────────────────────────────────────
export default function Sidebar({ open = false, collapsed = false, close }: { open?: boolean; collapsed?: boolean; close?: () => void }) {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const role = user?.role ?? 'viewer';
  // Subscribe so the menu re-renders once the effective permissions arrive.
  usePermissionStore((s) => s.permissions);
  const loadPermissions = usePermissionStore((s) => s.load);
  useEffect(() => {
    if (user?.id && !isSuperAdmin(role)) void loadPermissions(user.id);
  }, [user?.id, role, loadPermissions]);

  // Filter main nav groups by permissions
  const visibleGroups = navGroups.map(group => ({
    ...group,
    items: filterNav(group.items, role),
  })).filter(group => group.items.length > 0);

  // Filter settings nav by permissions
  const visibleSettings = filterNav(settingsNav, role);

  return (
    <aside data-open={open} data-collapsed={collapsed} className="hub-sidebar fixed left-0 top-0 z-50 flex h-full w-[240px] flex-col border-r border-outline-variant bg-surface-container-low p-4">
      <button onClick={close} className="md:hidden self-end" aria-label="Cerrar navegación">✕</button>

      {/* Branding */}
      <div className="mb-8 px-2">
        <h1 className="font-headline-md text-headline-md font-bold bg-gradient-to-r from-[#FF5E1E] to-[#D946EF] bg-clip-text text-transparent">
          BarmetHub
        </h1>
        <p className="font-label-md text-on-surface-variant">Operaciones BarmetMedia · v{APP_VERSION}</p>
      </div>

      {/* Navigation */}
      <nav className="flex-1 space-y-1 overflow-y-auto pr-2" style={{ scrollbarWidth: 'thin' }}>
        {visibleGroups.map(group => (
          <section key={group.id} aria-label={group.label}>
            <h2 className="px-3 pb-2 pt-4 text-[10px] uppercase tracking-widest text-outline">{group.label}</h2>
            {group.items.map(item => (
              <SidebarLink key={item.to} to={item.to} icon={item.icon} label={item.label} />
            ))}
          </section>
        ))}

        {/* Ajustes separator */}
        <div className="px-3 pb-2 pt-4">
          <span className="text-[10px] uppercase tracking-widest text-outline">Ajustes</span>
        </div>
        {visibleSettings.map(item => (
          <SidebarLink key={item.to} to={item.to} icon={item.icon} label={item.label} />
        ))}
      </nav>

      {/* User section */}
      <div className="mt-auto flex items-center gap-3 border-t border-outline-variant pt-4">
        <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-[#FF5E1E] to-[#D946EF] font-bold text-white text-xs">
          {user?.displayName?.charAt(0) || 'U'}
        </div>
        <div className="overflow-hidden">
          <p className="truncate font-label-md text-on-surface">{user?.displayName || 'Usuario'}</p>
          <p className="truncate text-[10px] text-on-surface-variant">
            {isSuperAdmin(role) ? 'SUPERADMIN' : role}
          </p>
        </div>
        <button
          aria-label="Cerrar sesión"
          onClick={() => { logout(); navigate('/login'); }}
          className="ml-auto text-on-surface-variant hover:text-[#FF5E1E]"
        >
          <MaterialIcon name="logout" className="text-sm" />
        </button>
      </div>
    </aside>
  );
}